/* eslint-disable complexity */
import { isEquipmentItem } from "common";
import { groupBy, keyBy, uniqueId } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { DigitalWaiver, Event, Experience, ExperiencePurchaseItem, FieldType, GuestStatus, Timeslot } from "types";
import { TimeslotWithMeta } from "../DashboardTable";
import { getOrganizerName } from "../Roster/RosterGuestList";
import { CatalogInfo, GuestRowData, SummaryData } from "../Roster/RosterGuestTypes";
import { calculateTotalWeightForItem, combineWeights } from "../Roster/weightHelpers";
import { getMemoizedExperience } from "./gridUtils";
import { GroupedData } from "./useDashboardTableData";
import { queryClient } from "@/api/queryClient";
import {
    getGuestsByStatus,
    getSummarizedGuestsData,
} from "@/components/PurchaseModification/CheckInPurchaseGuests/CheckInHelper";
import { hasUniqueGuestIdentification, isCheckInAllowed } from "@/models/Experience";
import { getPrimaryGuest } from "@/models/Guest";
import {
    getAddonLineItems,
    getAttendeeLineItemsForRoster,
    getDemographicLineItems,
    getEquipmentRosterLineItems,
} from "@/models/PurchaseItem";
import { isProductHasLinkedWaiverTemplate } from "@/models/Waiver";
import { DashboardFilter, DashboardGroupBy, DashboardGroupId } from "@/types/Dashboard";
import { getCollection } from "@/utils/getCollection";
import { getSeller } from "@/utils/getSeller";

export const getRowData = (
    items: ExperiencePurchaseItem[],
    filters: DashboardFilter,
): Array<GuestRowData | SummaryData> => {
    if (!items?.length) return [];

    const seller = getSeller();
    const sellerCustomerNotes = seller.customerNotes;

    const summary: SummaryData = {
        id: "summary",
        path: ["summary"],
        balance: 0,
        grossPurchaseAmount: 0,
        grossReservationAmount: 0,
        waiverQuantity: 0,
        waivers: [],
        demographics: { value: {}, length: 0 },
        equipments: { value: {}, length: 0 },
        addOns: { value: {}, length: 0 },
        customerNotesCount: 0,
        purchasesNoteCount: 0,
        currency: "",
        checkInStatus: {
            arrived: 0,
            pending: 0,
            noshow: 0,
            returned: 0,
        },
        product: [],
        time: [],
        isAttendeeUnique: true,
        groupedGuestsData: items ? getSummarizedGuestsData(items) : {},
    };

    const itemData: Array<GuestRowData | SummaryData> = [];

    // Need to sort data before populating rowData to ensure sorting in print view
    const sortedItems = sortPurchaseItems(items);
    const { all: isAllProductsSelected, selected: selectedProducts } = filters.products;

    const purchases = new Set<string>();
    let weightInKgs = 0;
    let weightInLbs = 0;
    for (const item of sortedItems) {
        if (!isAllProductsSelected && !selectedProducts.includes(item.product.id)) {
            continue; // Skip items not in selected products
        }
        const experience = getMemoizedExperience(item.product.id);
        const isWaiverLinked = experience ? doesProductHaveLinkedWaiver(experience) : false;
        const isCheckinEnabled = experience ? isCheckInAllowed(seller, experience) : false;
        const isAttendeeUnique = experience ? hasUniqueGuestIdentification(experience) : true;

        const [firstGuest] = item.guests;
        const primaryGuest = getPrimaryGuest(item.guests) ?? firstGuest;
        const purchaseNoteCount = item.purchase?.notes?.length ?? 0;
        // To avoid duplicates in the summary in case of MIST purchases
        if (!purchases.has(item.purchase.id)) {
            summary.purchasesNoteCount += purchaseNoteCount;
            summary.balance += item.purchase.balance ?? 0;
            summary.grossPurchaseAmount += item.purchase.amount ?? 0;
            purchases.add(item.purchase.id);
        }
        summary.grossReservationAmount += item.amount ?? 0;
        summary.waiverQuantity += isWaiverLinked ? item.quantity ?? 0 : 0;
        if (item.waivers?.length) summary.waivers.push(...item.waivers);
        summary.currency = item.purchase.currency ?? seller.currency;
        summary.product?.push(item.name);
        summary.time?.push(item.arrivalTime);
        const addOns: CatalogInfo = { value: {}, length: 0 };
        for (const merch of getAddonLineItems(item)) {
            if (merch.name && merch.quantity > 0) {
                summary.addOns.value[merch.name] = (summary.addOns?.value[merch.name] ?? 0) + (merch.quantity ?? 0);
                addOns.value[merch.name] = (addOns.value[merch.name] ?? 0) + (merch.quantity ?? 0);
            }
        }

        summary.addOns.length = Object.keys(summary.addOns.value).length;
        addOns.length = Object.keys(addOns.value).length;

        const demoLineItems = isEquipmentItem(item)
            ? getAttendeeLineItemsForRoster(item)
            : getDemographicLineItems(item);
        const equipmentLineItems = isEquipmentItem(item) ? getEquipmentRosterLineItems(item) : [];

        for (const demo of demoLineItems) {
            if (demo.quantity > 0) {
                summary.demographics.value[demo.name] = (summary.demographics.value[demo.name] ?? 0) + demo.quantity;
            }
        }
        summary.demographics.length = Object.keys(summary.demographics.value).length;

        for (const eq of equipmentLineItems) {
            if (eq.quantity > 0) {
                summary.equipments.value[eq.name] = (summary.equipments.value[eq.name] ?? 0) + eq.quantity;
            }
        }
        summary.equipments.length = Object.keys(summary.equipments.value).length;

        if (isCheckinEnabled) {
            const guestStatusMap = getGuestsByStatus(item);
            for (const key in guestStatusMap) {
                summary.checkInStatus[key as GuestStatus] += guestStatusMap[key as GuestStatus];
            }
        }

        const organizerId = item.purchase.organizer?.id ?? uniqueId("organizer");
        const organizerName = getOrganizerName(item);
        const itemDemographicsForOrganizer = isEquipmentItem(item)
            ? getAttendeeLineItemsForRoster(item)
            : getDemographicLineItems(item);
        const formattedDemo: { [key: string]: number } = {};
        for (const demographic of itemDemographicsForOrganizer) {
            if (demographic.quantity > 0) {
                formattedDemo[demographic.name] = demographic.quantity;
            }
        }
        const formattedEquipment: { [key: string]: number } = {};
        for (const eq of equipmentLineItems) {
            if (eq.quantity > 0) {
                formattedEquipment[eq.name] = eq.quantity;
            }
        }

        const { weightInKgs: itemWeightInKgs, weightInLbs: itemWeightInLbs } = calculateTotalWeightForItem(item);
        weightInKgs += itemWeightInKgs;
        weightInLbs += itemWeightInLbs;

        itemData.push({
            path: [item.id],
            organizer: { id: organizerId, name: organizerName },
            isOrganizer: true,
            purchaseNotes: item.purchase.notes,
            item,
            itemDemographics: { value: formattedDemo, length: Object.keys(formattedDemo).length },
            itemEquipments: { value: formattedEquipment, length: Object.keys(formattedEquipment).length },
            product: item.name,
            time: item.arrivalTime,
            isCheckInAllowed: isCheckinEnabled,
            isWaiverLinked,
            isAttendeeUnique,
            addOns,
            weight: combineWeights(itemWeightInKgs, itemWeightInLbs),
        });

        for (const guest of item.guests) {
            const isPrimaryGuest = guest === primaryGuest;
            const path = [item.id, guest.id];
            const travelerId = guest.traveler?.id ?? (isPrimaryGuest ? item.purchase.organizer?.id : undefined);
            const guestNotes =
                travelerId && sellerCustomerNotes ? Object.values(sellerCustomerNotes[travelerId] ?? []) : [];

            const demographic = itemDemographicsForOrganizer.find((d) => d.template.id === guest?.template?.id);

            if (guestNotes.length > 0) {
                summary.customerNotesCount++;
            }

            if (isAttendeeUnique) {
                itemData.push({
                    path,
                    guest,
                    travelerId,
                    demographic,
                    guestNotes,
                    item,
                    isCheckInAllowed: isCheckinEnabled,
                    isWaiverLinked,
                    isPrimaryGuest,
                });
            }
        }
    }

    summary.weight = combineWeights(weightInKgs, weightInLbs);

    return [summary, ...itemData];
};

export const groupTimeslots = (
    combined: TimeslotWithMeta[],
    groupByType: DashboardGroupBy,
): { groupTables: Array<{ groupLabel: string; items: TimeslotWithMeta[] }>; groupLabels: string[] } => {
    if (groupByType === DashboardGroupBy.NONE) {
        return { groupTables: [{ groupLabel: "All", items: combined }], groupLabels: ["All"] };
    }

    const groups: Record<string, TimeslotWithMeta[]> = {};
    const groupLabels: string[] = [];

    for (const item of combined) {
        let groupKeys: string[] = [];

        switch (groupByType) {
            case DashboardGroupBy.ARRIVAL_TIME:
                groupKeys = [item.purchaseItem?.arrivalTime?.toString() ?? DashboardGroupId.NO_TIME];
                break;

            case DashboardGroupBy.PRODUCT:
                groupKeys = [item.purchaseItem?.product?.id ?? item.timeslot.product ?? DashboardGroupId.NO_PRODUCT];
                break;

            case DashboardGroupBy.EVENT:
                groupKeys = [item.event?.id ?? "No Event"];
                break;

            case DashboardGroupBy.EQUIPMENT: {
                const resourceUsages = item.event?.resourceUsages ?? [];
                groupKeys =
                    resourceUsages.length > 0
                        ? resourceUsages.map((equipment) => equipment.resource.id)
                        : [DashboardGroupId.NO_EQUIPMENT];
                break;
            }

            case DashboardGroupBy.GUIDE: {
                const guides = item.event?.guides ?? [];
                groupKeys = guides.length > 0 ? guides.map((guide) => guide.guide.id) : [DashboardGroupId.NO_GUIDE];
                break;
            }

            case DashboardGroupBy.PICKUP: {
                const pickupField = item.purchaseItem?.formResponse?.fields.find(
                    (field) => field.type === FieldType.PICKUP,
                );
                const pickupLocationName =
                    pickupField?.pickupLocation?.name ??
                    item.purchaseItem?.pickupLocation ??
                    DashboardGroupId.NO_PICKUP;

                groupKeys = [pickupLocationName];
                break;
            }
            default:
                groupKeys = [DashboardGroupId.OTHER];
        }

        for (const key of groupKeys) {
            if (!groups[key]) {
                groups[key] = [];
            }
            if (!groupLabels.includes(key)) {
                groupLabels.push(key);
            }
            groups[key].push(item);
        }
    }

    return { groupTables: Object.entries(groups).map(([groupLabel, items]) => ({ groupLabel, items })), groupLabels };
};

const doesProductHaveLinkedWaiver = (experience: Experience) => {
    const waiverTemplates = getCollection<DigitalWaiver>("waiverTemplates");
    return experience ? isProductHasLinkedWaiverTemplate(waiverTemplates, experience) : false;
};

export const combineTimeslotsWithPurchaseItems = (
    timeslots: Timeslot[],
    purchaseItems?: ExperiencePurchaseItem[],
): TimeslotWithMeta[] => {
    const purchaseItemsByEventId = groupBy(purchaseItems, (item) => item.event?.id);
    return timeslots.flatMap((timeslot) => {
        const relatedPurchaseItems = purchaseItemsByEventId[timeslot.event?.id] ?? [];

        return relatedPurchaseItems.map((purchaseItem) => ({
            timeslot,
            purchaseItem,
        }));
    });
};

export const combineTimeslotsWithPurchaseItemsAndEvents = (
    timeslots: Timeslot[],
    purchaseItems?: ExperiencePurchaseItem[],
): TimeslotWithMeta[] => {
    const events = getEvents(timeslots);
    const purchaseItemsByEventId = groupBy(purchaseItems, (item) => item.event?.id);
    const eventsById = keyBy(events, (event) => event.id);

    return timeslots.flatMap((timeslot) => {
        const relatedPurchaseItems = purchaseItemsByEventId[timeslot.event?.id] ?? [];
        const event = eventsById[timeslot.event?.id];

        return relatedPurchaseItems.map((purchaseItem) => ({
            timeslot,
            purchaseItem,
            event: event,
        }));
    });
};

const sortPurchaseItems = (items: ExperiencePurchaseItem[] | undefined) => {
    if (!items) return [];
    return [...items].sort((a, b) => {
        const timeA = a.arrivalTime ?? -1;
        const timeB = b.arrivalTime ?? -1;
        if (timeA !== timeB) return timeA - timeB;

        const productA = a.name?.toLowerCase() ?? "";
        const productB = b.name?.toLowerCase() ?? "";
        if (productA !== productB) return productA.localeCompare(productB);

        const guestA = getOrganizerName(a).toLowerCase() ?? "";
        const guestB = getOrganizerName(b).toLowerCase() ?? "";
        return guestA.localeCompare(guestB);
    });
};

export const sortGroupedData = (groupedData: GroupedData[], groupHeaderData: any): GroupedData[] => {
    return groupedData.sort((a, b) => {
        const aMeta = groupHeaderData[a.groupLabel];
        const bMeta = groupHeaderData[b.groupLabel];

        const aKey = aMeta?.sortKey ?? a.groupLabel;
        const bKey = bMeta?.sortKey ?? b.groupLabel;

        const isANoGroup = aKey.startsWith("No ") || aKey === DashboardGroupId.OTHER;
        const isBNoGroup = bKey.startsWith("No ") || bKey === DashboardGroupId.OTHER;

        if (isANoGroup && !isBNoGroup) return 1;
        if (!isANoGroup && isBNoGroup) return -1;
        return aKey.localeCompare(bKey);
    });
};

export const useVisibleObserver = () => {
    const [visibleMap, setVisibleMap] = useState<Record<string, boolean>>({});
    const observerRef = useRef<IntersectionObserver | null>(null);
    const observedElements = useRef<Set<Element>>(new Set());

    const observe = useCallback((id: string, element: HTMLElement | null) => {
        if (!element) return;

        if (!observerRef.current) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    setVisibleMap((previous) => {
                        const updated = { ...previous };
                        for (const entry of entries) {
                            const key = (entry.target as HTMLElement).dataset.groupId;
                            if (key && entry.isIntersecting) {
                                updated[key] = true;
                            }
                        }
                        return updated;
                    });
                },
                {
                    root: null,
                    rootMargin: "100px 0px 100px 0px", // 100px above and below
                    threshold: 0.01,
                },
            );
        }

        element.dataset.groupId = id;

        if (!observedElements.current.has(element)) {
            observerRef.current.observe(element);
            observedElements.current.add(element);
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        const currentObserver = observerRef.current;
        const currentElements = observedElements.current;

        return () => {
            if (currentObserver) {
                currentObserver.disconnect();
            }
            currentElements.clear();
        };
    }, []);

    return { visibleMap, observe };
};

const getEvents = (timeslots: Timeslot[]): Event[] => {
    const timeslotsWithEvents = timeslots.filter((t) => !!t.event?.id);

    return timeslotsWithEvents
        .map((t) => queryClient.getQueryData<Event>(["events", t.event?.id]))
        .filter((event): event is Event => !!event);
};
